import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BulletinLikeCornerProps {
  onLike: () => void;
  isLiked: boolean;
  isVisible?: boolean;
  className?: string;
}

const BulletinLikeCorner: React.FC<BulletinLikeCornerProps> = ({
  onLike,
  isLiked,
  isVisible = true,
  className
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    console.log('[BulletinLikeCorner] Click detected');
    e.preventDefault();
    e.stopPropagation();
    
    if (isLiked) return;
    
    setIsAnimating(true);
    onLike();
    
    // Reset animation after completion
    setTimeout(() => setIsAnimating(false), 600);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    console.log('[BulletinLikeCorner] Touch start detected');
    e.preventDefault();
    e.stopPropagation();
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    console.log('[BulletinLikeCorner] Touch end detected');
    e.preventDefault();
    e.stopPropagation();
    
    if (isLiked) return;
    
    setIsAnimating(true);
    onLike();
    
    // Reset animation after completion
    setTimeout(() => setIsAnimating(false), 600);
  };

  if (!isVisible) return null;

  return (
    <div 
      className={cn(
        "absolute bottom-0 right-0 w-12 h-12 z-[70] cursor-pointer",
        "pointer-events-auto touch-manipulation",
        className
      )}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{ 
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none'
      }}
    >
      {/* Gradient circle background */}
      <div className={cn(
        "absolute inset-0 opacity-40",
        "bg-gradient-radial from-center",
        isLiked ? "from-red-500/60 via-red-400/20 to-transparent" : "from-primary/40 via-primary/10 to-transparent",
        "rounded-full transition-all duration-300"
      )}>
        {/* Glow effect */}
        <div className={cn(
          "absolute inset-0 opacity-30",
          "bg-gradient-radial from-white/10 via-white/5 to-transparent",
          "rounded-full animate-pulse"
        )} />
      </div>

      {/* Heart icon */}
      <div className="absolute bottom-3 right-3 flex items-center justify-center">
        <Heart 
          className={cn(
            "w-3 h-3 transition-all duration-200 opacity-30",
            isLiked ? "text-red-500 fill-current scale-110" : "text-white"
          )}
        />
      </div>

      {/* Like animation */}
      <AnimatePresence>
        {isAnimating && (
          <>
            {/* Ripple effect */}
            <motion.div
              initial={{ scale: 0, opacity: 1 }}
              animate={{ scale: 3, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="absolute bottom-2 right-2 w-4 h-4 border-2 border-red-500 rounded-full"
            />
            
            {/* Heart burst */}
            <motion.div
              initial={{ scale: 1, opacity: 1 }}
              animate={{ scale: 1.5, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="absolute bottom-2 right-2"
            >
              <Heart className="w-4 h-4 text-red-500 fill-current" />
            </motion.div>

            {/* Floating hearts */}
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ 
                  scale: 0, 
                  opacity: 1,
                  x: 0,
                  y: 0,
                  rotate: 0
                }}
                animate={{ 
                  scale: [0, 1, 0], 
                  opacity: [1, 1, 0],
                  x: (i - 1) * 15,
                  y: -20 - (i * 10),
                  rotate: (i - 1) * 15
                }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.8, 
                  delay: i * 0.1,
                  ease: "easeOut" 
                }}
                className="absolute bottom-2 right-2"
              >
                <Heart className="w-2 h-2 text-red-400 fill-current" />
              </motion.div>
            ))}
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BulletinLikeCorner;